import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, AlertCircle, Loader, CheckCircle } from "lucide-react";
import { toast } from "react-hot-toast";
import { forgotPassword } from "../../services/auth.service";
import { validateAllMessages, REGEX } from "../../utils/validation";
import { VALIDATION_MESSAGES } from "../../constants/messages";

const validationSchema = {
  email: [
    { test: (v: string) => v.trim().length > 0, message: VALIDATION_MESSAGES.email.required },
    { test: (v: string) => REGEX.email.test(v), message: VALIDATION_MESSAGES.email.invalid },
  ],
};

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState<Record<string, string[]>>({ email: [] });
  const [touched, setTouched] = useState<Record<string, boolean>>({ email: false });
  const [apiError, setApiError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const validateField = (field: string, value: string): string[] => {
    const rules = validationSchema[field as keyof typeof validationSchema];
    if (!rules) return [];
    const fieldErrors: string[] = [];
    for (const rule of rules) {
      if (!rule.test(value)) {
        fieldErrors.push(rule.message);
      }
    }
    return fieldErrors;
  };

  const handleChange = (value: string) => {
    setEmail(value);
    setApiError("");
    if (touched.email) {
      setErrors((prev) => ({ ...prev, email: validateField("email", value) }));
    }
  };

  const handleBlur = () => {
    setTouched((prev) => ({ ...prev, email: true }));
    setErrors((prev) => ({ ...prev, email: validateField("email", email) }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError("");

    const allErrors = validateAllMessages({ email }, validationSchema);
    setErrors(allErrors);
    setTouched({ email: true });

    const hasErrors = Object.values(allErrors).some((msgs) => msgs.length > 0);
    if (hasErrors) return;

    setIsLoading(true);
    try {
      const { hostname } = window.location;
      const subdomain =
        hostname.includes("diagnomiq.ai") && hostname.split(".")[0] !== "diagnomiq"
          ? hostname.split(".")[0]
          : undefined;

      await forgotPassword(email, subdomain);
      setIsSuccess(true);
      toast.success("Password reset link sent to your email");
    } catch (err: any) {
      const msg = err?.response?.data?.message || "Failed to send reset link";
      setApiError(msg);
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full space-y-8 py-12">
          <div className="text-center">
            <img
              src="/logo/DiagnomIQ.png"
              alt="DiagnomIQ"
              className="mx-auto h-24 w-auto object-contain"
            />
          </div>
          <div className="bg-white py-8 px-6 shadow-xl rounded-lg border border-gray-100">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <h2 className="mt-4 text-lg font-semibold text-gray-900">Check your email</h2>
              <p className="mt-2 text-sm text-gray-600">
                If an account exists with <strong>{email}</strong>, we've sent a password reset link.
              </p>
              <p className="mt-4 text-sm text-gray-500">
                Didn't receive the email? Check your spam folder or{' '}
                <button
                  onClick={() => {
                    setIsSuccess(false);
                    setEmail("");
                  }}
                  className="text-primary-600 hover:text-primary-500 font-medium"
                >
                  try again
                </button>
              </p>
            </div>
            <div className="mt-6">
              <Link
                to="/login"
                className="w-full flex justify-center py-2.5 px-4 border border-neutral-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-300 transition-colors"
              >
                Back to Sign in
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen w-full flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full space-y-8 py-12">
        <div className="text-center">
          <img
            src="/logo/DiagnomIQ.png"
            alt="DiagnomIQ"
            className="mx-auto h-24 w-auto object-contain"
          />
          <h2 className="mt-4 text-xl font-bold text-gray-900">Forgot your password?</h2>
          <p className="mt-2 text-sm text-gray-600">
            Enter your email address and we'll send you a link to reset your password.
          </p>
        </div>

        <div className="bg-white py-8 px-6 shadow-xl rounded-lg border border-gray-100">
          {apiError && (
            <div className="mb-4 flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {apiError}
            </div>
          )}

          <form className="space-y-6" onSubmit={handleSubmit} noValidate>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email Address
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => handleChange(e.target.value)}
                  onBlur={handleBlur}
                  className={`pl-10 py-3 block w-full rounded-md sm:text-sm transition-colors ${
                    touched.email && errors.email?.length
                      ? "border-red-300 text-red-900 placeholder-red-400 focus:ring-red-500 focus:border-red-500"
                      : "border-neutral-300 focus:ring-primary-300 focus:border-primary-500"
                  }`}
                  placeholder="Enter your email"
                  autoComplete="email"
                  aria-invalid={!!(touched.email && errors.email?.length)}
                  aria-describedby={errors.email?.length ? "email-error" : undefined}
                />
              </div>
              {touched.email && errors.email?.length > 0 && (
                <div id="email-error" className="mt-1 space-y-0.5">
                  {errors.email.map((msg, i) => (
                    <p key={i} className="text-xs text-red-600 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3 flex-shrink-0" />
                      {msg}
                    </p>
                  ))}
                </div>
              )}
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="group relative w-full flex justify-center py-2.5 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-500 hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <Loader className="h-4 w-4 mr-2 animate-spin" />
                    Sending reset link...
                  </>
                ) : (
                  "Send reset link"
                )}
              </button>
            </div>
          </form>

          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="text-sm text-primary-600 hover:text-primary-500 font-medium"
            >
              Back to Sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
